import React from 'react';
import { createPortal } from 'react-dom';
import DateTimePopover, { dateFieldTitle } from '../../../../../ui/DateTimePopover';
import {
  getEffectiveDurationMinutes,
  formatDurationMinutes,
  formatDurationRange,
} from '../../../../../../utils/taskDuration';
import { parseMissionDate } from '../../../../../../utils/missionDates';
import { EstimatedDurationInputs } from './EstimatedDurationInputs';

export function DueDateFieldRow({
  field,
  task,
  classPrefix,
  estimatedMinutes,
  estimatedMinMinutes,
  estimatedMaxMinutes,
  isNestedDurationRollup = false,
  openPicker,
  pickerPosition,
  tooltipField,
  tooltipPosition,
  valueRefs,
  portalRef,
  setTooltipField,
  handleFieldClick,
  handleFieldMouseLeave,
  handleDateChange,
  handleClear,
  onDismissPicker,
}) {
  const hasHandler = !!field.handler;
  const hasValue = !!field.displayValue;
  const IconComponent = field.icon;
  const isPickerOpen = openPicker === field.id;
  const isDisabledInTemplate = field.disabledInTemplate;
  const isTooltipOpen = tooltipField === field.id;

  const durationMinutes =
    field.inputType === 'duration' && estimatedMinutes != null
      ? estimatedMinutes
      : getEffectiveDurationMinutes(task);
  const hasDurationRange = isNestedDurationRollup
    && estimatedMaxMinutes != null
    && (estimatedMinMinutes ?? 0) !== estimatedMaxMinutes;
  const nestedRangeText = isNestedDurationRollup
    ? (estimatedMaxMinutes != null
      ? formatDurationRange(estimatedMinMinutes ?? 0, estimatedMaxMinutes)
      : '—')
    : undefined;
  const durationTitle =
    field.inputType === 'duration' && durationMinutes
      ? formatDurationMinutes(durationMinutes)
      : undefined;
  const nestedRollupTitle = isNestedDurationRollup
    ? (hasDurationRange
      ? 'Fastest to longest route through open choices'
      : 'Total estimated time from missions in nested campaign')
    : undefined;
  const rowTitle =
    field.inputType === 'duration'
      ? nestedRollupTitle || durationTitle
      : field.value
        ? new Date(field.value).toLocaleString()
        : isDisabledInTemplate
          ? field.templateTooltip
          : undefined;

  // Due-status tint applies only to the value, not the row/label (avoids inherit / quick-view issues).
  const valueStatusClass =
    (field.inputType === 'date' || field.inputType === 'datetime') &&
    field.statusClass &&
    field.statusClass !== 'normal'
      ? `${classPrefix}__date-row-value--${field.statusClass}`
      : '';

  return (
    <div
      className={`${classPrefix}__date-row ${hasHandler ? `${classPrefix}__date-row--editable` : ''} ${isDisabledInTemplate ? `${classPrefix}__date-row--disabled` : ''}`}
      title={rowTitle}
    >
      <span className={`${classPrefix}__date-row-label`}>{field.label}</span>

      {hasHandler && field.inputType === 'duration' && (
        <div className={`${classPrefix}__date-row-controls`}>
          <EstimatedDurationInputs
            task={task}
            minutes={durationMinutes}
            readOnly={isNestedDurationRollup}
            readOnlyText={nestedRangeText}
            readOnlyTitle={nestedRollupTitle}
            classPrefix={classPrefix}
            onEstimatedDurationMinutesChange={field.handler}
          />
        </div>
      )}

      {(field.inputType === 'date' || field.inputType === 'datetime') && (
        <span
          ref={(el) => {
            valueRefs.current[field.id] = el;
          }}
          className={`${classPrefix}__date-row-value ${valueStatusClass} ${hasHandler ? `${classPrefix}__date-row-value--clickable` : ''} ${!hasValue ? `${classPrefix}__date-row-value--placeholder` : ''} ${isDisabledInTemplate ? `${classPrefix}__date-row-value--disabled` : ''} ${field.readOnly ? `${classPrefix}__date-row-value--readonly` : ''}`}
          onClick={handleFieldClick(field)}
          onMouseDown={(e) => e.stopPropagation()}
          onMouseLeave={handleFieldMouseLeave(field)}
        >
          <span className={`${classPrefix}__date-row-value-inner`}>
            {!hasValue && IconComponent && <IconComponent size={10} />}
            <span>{field.displayValue || field.placeholder || '—'}</span>
          </span>
        </span>
      )}

      {isTooltipOpen &&
        field.templateTooltip &&
        createPortal(
          <div
            className={`${classPrefix}__template-tooltip`}
            style={{
              position: 'fixed',
              top: tooltipPosition.top,
              left: tooltipPosition.left,
              zIndex: 10000,
            }}
            onClick={(e) => {
              e.stopPropagation();
              setTooltipField(null);
            }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            {field.templateTooltip}
          </div>,
          document.body
        )}

      {isPickerOpen &&
        (field.inputType === 'date' || field.inputType === 'datetime') &&
        createPortal(
          <div
            ref={portalRef}
            className={`${classPrefix}__date-picker-portal`}
            style={{
              position: 'fixed',
              top: pickerPosition.top,
              left: pickerPosition.left,
              zIndex: 10000,
            }}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <DateTimePopover
              title={dateFieldTitle(field.label)}
              chooseMode={field.id === 'dueDate'}
              countdownCommits={field.id === 'dueDate'}
              countdownApplyLabel="Set due date"
              selected={field.value ? parseMissionDate(field.value) : null}
              onChange={handleDateChange(field)}
              onDismiss={onDismissPicker}
              onClear={handleClear(field)}
              showClear
              showTimeSelect={field.showTime}
            />
          </div>,
          document.body
        )}
    </div>
  );
}
